import { div, type Mountable, type Renderable } from '@llui/dom';

/**
 * `<Card>` — surface-elevated panel primitive.
 *
 * The `surface-elevated + border + radius + padding` pattern recurs
 * across .auth-card, .settings-section, .draft-card, .substrate-
 * dialog (inside the modal), the design-system mock's .person-card,
 * etc. The atom consolidates the chrome; variants pick the semantic
 * treatment (default = surface-elevated; sunken = surface-sunken
 * for nested detail; draft = draft-bg + draft-text + 3px inset rail
 * per 30 §7.1 row-level status overlay).
 *
 * The atom only owns chrome. Composition with `formField`, headings,
 * actions, etc. is the caller's responsibility.
 *
 * Construction:
 *
 *   card({
 *     variant: 'default',
 *     padding: 'lg',
 *     children: [ h1(['Sign in']), formField({...}) ],
 *   })
 */

export type CardVariant = 'default' | 'sunken' | 'draft';
export type CardPadding = 'sm' | 'md' | 'lg' | 'xl' | 'none';

export interface CardOptions {
  /** Visual treatment — default / sunken / draft. */
  variant?: CardVariant;
  /** Internal padding scale; defaults to 'md'. */
  padding?: CardPadding;
  /** Optional CSS class for app-specific extension. */
  className?: string;
  /** Forwarded `data-testid`. */
  testid?: string;
  /** Children rendered inside the card. */
  children: Renderable;
}

export function card(opts: CardOptions): Mountable {
  const variant: CardVariant = opts.variant ?? 'default';
  const padding: CardPadding = opts.padding ?? 'md';
  const klass = `dl-card dl-card--${variant} dl-card--p-${padding}${opts.className ? ` ${opts.className}` : ''}`;
  return div(
    {
      class: klass,
      'data-variant': variant,
      'data-testid': opts.testid,
    },
    opts.children,
  );
}
